import type { LucideIcon } from 'lucide-react'

import {
    BellRing,
    ChartLine,
    FileCheck2,
    Handshake,
    LayoutDashboard,
    RadioTower,
    ShieldCheck,
    Thermometer
} from 'lucide-react'

export interface Benefit {
    icon: LucideIcon
    label: string
    title: string
    text: string
    points: Array<string>
}

export interface ServiceModule {
    number: string
    icon: LucideIcon
    title: string
    tagline: string
    points: Array<string>
    note?: string
}

export interface DataFlowStep {
    icon: LucideIcon
    title: string
    text: string
    via?: string
}

export interface TechFact {
    title: string
    text: string
}

export type DrawingVariant = 'wanne' | 'massiv' | 'platte' | 'tiefgarage' | 'abschnitte'

export interface UseCase {
    drawing: DrawingVariant
    title: string
    text: string
}

export interface ProcessStep {
    number: string
    title: string
    text: string
    deliverables: Array<string>
}

export interface Fact {
    value: string
    label: string
}

export interface TeamMember {
    initials: string
    name: string
    role: string
    text: string
    focus: Array<string>
    linkedin: string
}

export interface Faq {
    question: string
    answer: Array<string>
}

export const HERO_POINTS: Array<string> = [
    "Live-Temperaturüberwachung",
    "Frühwarnung bei Rissrisiko",
    "Prüffähige Dokumentation"
]

export const STANDARDS: Array<{ name: string, detail: string }> = [
    { name: "DAfStb-WU-Richtlinie", detail: "Wasserundurchlässige Bauwerke aus Beton" },
    { name: "DIN EN 13670", detail: "Ausführung von Tragwerken aus Beton" },
    { name: "Zement-Merkblatt B11", detail: "Massige Bauteile aus Beton" }
]

export const BENEFITS: Array<Benefit> = [
    {
        icon: ShieldCheck,
        label: "Schadenprävention",
        title: "Temperaturrisse erkennen, bevor sie entstehen.",
        text: "Kontinuierliche Messwerte zeigen kritische Temperaturgradienten früh. So greifen Nachbehandlungsmaßnahmen, bevor Risse aufwendig saniert werden müssen.",
        points: [
            "Live-Werte je Abschnitt und Sonde",
            "Frühwarnung bei kritischen Temperaturdifferenzen",
            "Weniger Nacharbeit und Sanierungsaufwand"
        ]
    },
    {
        icon: ChartLine,
        label: "Datenbasierte Entscheidungen",
        title: "Entscheiden auf Basis gemessener Verläufe.",
        text: "Bauleitung oder Prüfingenieur entscheiden anhand echter Temperaturkurven statt allein nach Erfahrungswert. ConcreTrack liefert die Daten, die Entscheidung bleibt bei Ihnen.",
        points: [
            "Alle Beteiligten sehen denselben Datenstand",
            "Verläufe statt Momentaufnahmen",
            "Weniger Rückfragen, schnellere Abstimmung"
        ]
    },
    {
        icon: FileCheck2,
        label: "Prüffähige Dokumentation",
        title: "Ein Nachweis, der bei der Abnahme trägt.",
        text: "Jeder Messwert wird lückenlos erfasst und für die Übergabe strukturiert aufbereitet, zugeordnet zu Bauteil und Betonierabschnitt.",
        points: [
            "Vollständige Messhistorie je Projekt",
            "Strukturierte Übergabe an den Auftraggeber",
            "Lückenlose Ablage ohne Excel-Nachbau"
        ]
    }
]

export const SERVICE_MODULES: Array<ServiceModule> = [
    {
        number: "01",
        icon: Thermometer,
        title: "Sensorik vor Ort",
        tagline: "Robuste Hardware mit nachvollziehbarer Platzierung.",
        points: [
            "Messkonzept je Bauabschnitt",
            "Montage- und Einbauprotokoll",
            "Stabile Datenerfassung unter Baustellenbedingungen"
        ]
    },
    {
        number: "02",
        icon: LayoutDashboard,
        title: "Analyseplattform",
        tagline: "Temperaturverläufe, Dokumentation und Reifekurven in einer Ansicht.",
        points: [
            "Live-Dashboard je Projekt und Abschnitt",
            "Verläufe je Sonde und Abschnitt",
            "Dokumentation für Auftraggeber und Qualitätssicherung"
        ],
        note: "Reifekurven dienen der Orientierung: näherungsweise, ohne rezepturspezifische Kalibrierung."
    },
    {
        number: "03",
        icon: Handshake,
        title: "Laufende Begleitung",
        tagline: "Persönlicher Ansprechpartner statt Ticket-Pingpong.",
        points: [
            "Gemeinsame Inbetriebnahme",
            "Praxisnahe Einweisung Ihres Teams",
            "Kontinuierliche Betreuung während des Projekts"
        ]
    }
]

export const DATA_FLOW: Array<DataFlowStep> = [
    {
        icon: Thermometer,
        title: "Sensor im Frischbeton",
        text: "Misst direkt im Bauteil, nicht an der Oberfläche oder in der Umgebungsluft.",
        via: "per Funk"
    },
    {
        icon: RadioTower,
        title: "Gateway auf der Baustelle",
        text: "Sammelt die Messwerte aller Sonden und überträgt sie per Mobilfunk.",
        via: "per Mobilfunk"
    },
    {
        icon: LayoutDashboard,
        title: "ConcreTrack-Plattform",
        text: "Verläufe je Sonde und Abschnitt, Grenzwerte und Rollen in einem Dashboard.",
        via: "in Echtzeit"
    },
    {
        icon: BellRing,
        title: "Warnung und Nachweis",
        text: "Sofortige Warnung bei Grenzwertüberschreitung und lückenlose Historie für die Abnahme."
    }
]

export const TECH_FACTS: Array<TechFact> = [
    {
        title: "Kein WLAN nötig",
        text: "Funk zum Gateway, von dort per Mobilfunk. Sie bauen keine eigene Infrastruktur auf."
    },
    {
        title: "Dichte Messkurven",
        text: "Gerade in den ersten 24 bis 72 Stunden der Hydratation, wenn sich der Gradient aufbaut."
    },
    {
        title: "Zweites Paar Augen",
        text: "Unser Team behält die Daten während der Messung zusätzlich im Blick."
    },
    {
        title: "Redundante Platzierung",
        text: "Fällt eine Sonde aus, bleibt die Messreihe in der Regel belastbar. Die Störung wird dokumentiert."
    }
]

export const USE_CASES: Array<UseCase> = [
    {
        drawing: 'wanne',
        title: "WU-Beton und Weiße Wanne",
        text: "Bei der Weißen Wanne ist das Temperaturmonitoring Teil des WU-Konzepts nach DAfStb-Richtlinie, denn jeder Trennriss ist ein möglicher Wasserweg."
    },
    {
        drawing: 'massiv',
        title: "Massige Bauteile",
        text: "Dicke Wände, Widerlager und Fundamentblöcke entwickeln im Kern viel Wärme. Die Messung zeigt, wann gegengesteuert werden muss."
    },
    {
        drawing: 'platte',
        title: "Bodenplatten und Fundamente",
        text: "Großflächige Betonagen mit Messpunkten in Kern und Randzone und einem lückenlosen Verlauf bis zur Freigabe."
    },
    {
        drawing: 'tiefgarage',
        title: "Tiefgaragen",
        text: "Bodenplatte, Wände und Decken mit unterschiedlichen Dicken und Betonierzeitpunkten, sauber getrennt dokumentiert."
    },
    {
        drawing: 'abschnitte',
        title: "Großprojekte von Generalunternehmern",
        text: "Viele Abschnitte, viele Beteiligte: ein gemeinsamer Datenstand für Bauleitung, Auftraggeber und Qualitätssicherung."
    }
]

export const PROCESS_STEPS: Array<ProcessStep> = [
    {
        number: "01",
        title: "Beratung",
        text: "Wir klären Ziele, Randbedingungen und Entscheidungslogik gemeinsam mit Bauleitung und Betontechnologie.",
        deliverables: ["Projektbriefing", "Messstrategie je Abschnitt"]
    },
    {
        number: "02",
        title: "Installation",
        text: "Sensoren und Gateway werden vor Ort eingebaut und getestet, idealerweise vor dem Betoniertermin.",
        deliverables: ["Installationsprotokoll", "Abnahme der Kommunikationsstrecke"]
    },
    {
        number: "03",
        title: "Konfiguration",
        text: "Dashboard, Grenzwerte und Rollen richten wir passend zu Ihrem Projekt und Ihren Vorgaben ein.",
        deliverables: ["Projekt-Dashboard", "Alarm- und Reporting-Setup"]
    },
    {
        number: "04",
        title: "Betrieb und Support",
        text: "Wir begleiten die Messung laufend, optimieren bei Bedarf und übergeben die Dokumentation.",
        deliverables: ["Regeltermine nach Bedarf", "Support mit definierten Verantwortlichkeiten"]
    }
]

export const FACTS: Array<Fact> = [
    { value: "24–72 h", label: "kritische Hydratationsphase, lückenlos gemessen" },
    { value: "15–20 K", label: "Warnschwelle für die Kern-Rand-Differenz, je nach Vorgabe" },
    { value: "ab ca. 80 cm", label: "Bauteildicke wird Monitoring in der Regel relevant" },
    { value: "DACH", label: "Einsatz in Deutschland, Österreich und der Schweiz" }
]

export const TEAM: Array<TeamMember> = [
    {
        initials: "JB",
        name: "Dr.-Ing. Jörn Budde",
        role: "Geschäftsführer",
        text: "Langjährige Bauerfahrung mit Fokus auf zuverlässige Ausführung und hohe Qualitätsstandards.",
        focus: ["Bauprozesse und Methodik", "Qualitätssicherung", "Projektverantwortung auf Leitungsebene"],
        linkedin: "https://www.linkedin.com/in/joern-budde"
    },
    {
        initials: "MB",
        name: "B. Sc. Max Budde (WI)",
        role: "Leitung Produkt und Software",
        text: "Wirtschaftsinformatiker, der Baustellenrealität und digitale Produktentwicklung zusammenbringt.",
        focus: ["Produktstrategie", "Plattform- und Datenlogik", "Technischer Kundendialog"],
        linkedin: "https://www.linkedin.com/in/max-budde-wirt-inf"
    }
]

export const FAQS: Array<Faq> = [
    {
        question: "Wie schnell kann ein Projekt starten?",
        answer: [
            "In der Regel beginnt die Messung innerhalb weniger Werktage nach dem ersten Gespräch. Messstrategie und Sensorpositionen legen wir je Abschnitt gemeinsam fest und stimmen die Logistik mit Bauleitung und Betontechnologie ab.",
            "Gerade bei massigen Bauteilen wie Bodenplatten, Widerlagern oder dicken Wänden zählen die ersten Stunden. Deshalb werden die Sensoren idealerweise vor dem Betoniertermin eingebaut. Hardware, Installationsplan und Inbetriebnahme kommen von uns, Sie bauen keine eigene Infrastruktur auf.",
            "Bei kurzfristigen Einsätzen priorisieren wir die Anreise, damit die Messung mit der ersten kritischen Betonage startet und keine Datenlücke entsteht. Auf Wunsch begleiten wir die erste Betonage vor Ort."
        ]
    },
    {
        question: "Wie verlässlich sind die Messungen auf der Baustelle?",
        answer: [
            "Gemessen wird kontinuierlich direkt im Frischbeton, nicht über Umgebungs- oder Oberflächensensoren. Die Sensoren funken an ein Gateway, das die Daten per Mobilfunk weiterleitet. Ein WLAN auf der Baustelle ist nicht nötig.",
            "Während der Hydratation, typischerweise in den ersten 24 bis 72 Stunden, entstehen dichte Verläufe, die den Gradienten zwischen Kern und Rand sichtbar machen. Überschreitet die Kern-Rand-Differenz den festgelegten Grenzwert, je nach Vorgabe 15 bis 20 Kelvin, wird sofort gewarnt. Zusätzlich behält unser Team die Daten während der Messung im Blick."
        ]
    },
    {
        question: "Welche Daten erhalten Bauleitung und Auftraggeber?",
        answer: [
            "Beide arbeiten mit demselben Datenstand: Live-Verläufe je Sonde und Abschnitt, der Status von Grenzwertüberschreitungen und eine exportfähige Messhistorie.",
            "Für Nachweise nach DAfStb-WU-Richtlinie oder projektspezifischen Qualitätsplänen verbindet die Dokumentation Temperaturverläufe, Messpositionen und Ereignisprotokolle. Alles ist Bauteil und Betonierabschnitt zugeordnet statt als lose Excel-Sammlung abgelegt, sodass Entscheidungen für Prüfingenieure nachvollziehbar bleiben, etwa mit Blick auf DIN EN 13670.",
            "Das reduziert Rückfragen, verkürzt Freigabeschleifen und erspart die Suche nach Protokollen kurz vor der Abnahme."
        ]
    },
    {
        question: "Wie aufwendig ist die Einarbeitung für mein Team?",
        answer: [
            "Das System ist für Baustellenpersonal gemacht, nicht für Softwarespezialisten. In der Regel genügen eine Einweisung vor Ort und eine begleitete erste Betonage.",
            "Dabei geht es auch um die Interpretation: Ab wann wird ein Gradient kritisch, welche Nachbehandlung wirkt, und wann ist Abdecken oder Dämmen die richtige Reaktion? Wir schulen entlang Ihrer tatsächlichen Abläufe statt mit allgemeinen Handbüchern. Ihr persönlicher Ansprechpartner bleibt auch im laufenden Betrieb erreichbar."
        ]
    },
    {
        question: "Was passiert bei Störungen oder Ausfällen?",
        answer: [
            "Störungen bearbeiten wir über einen definierten Supportprozess mit klaren Zuständigkeiten und benannten Ansprechpartnern für die Eskalation. Das ist vor allem bei der Weißen Wanne wichtig, wo das Temperaturmonitoring Teil des WU-Konzepts nach DAfStb-Richtlinie ist.",
            "Sendet eine Sonde nicht mehr, ist das im Verlauf des Abschnitts im Dashboard sofort sichtbar. Durch redundante Sensorplatzierung bleibt die Messreihe in der Regel belastbar. In der Abschlussdokumentation werden Störungen und Gegenmaßnahmen transparent ausgewiesen, ohne die Aussagekraft der gesamten Messung zu gefährden."
        ]
    },
    {
        question: "Welche Normen und Richtlinien sind relevant?",
        answer: [
            "Maßgebliche Grundlage ist die DAfStb-Richtlinie für wasserundurchlässige Bauwerke aus Beton, die Temperaturüberwachung als Teil der Qualitätssicherung im WU-Bau vorsieht. DIN EN 13670 regelt unter anderem Nachbehandlung und Temperaturführung während der Erhärtung, und das Zement-Merkblatt B11 gibt Hinweise zur Begrenzung der Hydratationswärme in massigen Bauteilen.",
            "ConcreTrack unterstützt die Dokumentationsanforderungen aller drei."
        ]
    },
    {
        question: "Wann ist ein Temperaturmonitoring erforderlich?",
        answer: [
            "Immer dann, wenn die Hydratationswärme zu kritischen Temperaturdifferenzen zwischen Kern und Rand führen kann. Das ist typischerweise ab etwa 80 cm Bauteildicke der Fall oder beim Einsatz von Zementen mit hoher Wärmeentwicklung.",
            "Bei Weißen Wannen, massigen Fundamenten, Bodenplatten und Tiefgaragen ist es fachlich geboten und wird von Auftraggebern und Prüfingenieuren häufig gefordert. Auch bei extremer Hitze oder Frost hilft die Messung bei der Entscheidung, ob abgedeckt, gedämmt oder gekühlt werden muss."
        ]
    },
    {
        question: "Wie läuft die Zusammenarbeit ab und was kostet sie?",
        answer: [
            "Wir arbeiten projektbezogen. Das Angebot richtet sich nach der Anzahl der Bauteile, der Abschnitte und der Projektdauer und umfasst Sensorik, Plattformzugang, Inbetriebnahme und persönliche Betreuung.",
            "Die Kosten skalieren mit Größe und Komplexität, vom einzelnen Abschnitt bis zu Projekten mit mehreren Dutzend Messstellen. Vor jedem Angebot steht ein unverbindliches Erstgespräch."
        ]
    }
]

export const CONSULTATION_TOPICS: Array<string> = [
    "Bauteile, Abschnitte und Betoniertermine",
    "Grenzwerte und Anforderungen an den Nachweis",
    "Sensorpositionen und Logistik vor Ort",
    "Ein Angebot passend zu Umfang und Dauer"
]

export const PROJECT_TYPES: Array<string> = [
    "WU-Beton oder Weiße Wanne",
    "Bodenplatte oder Fundament",
    "Massiges Bauteil",
    "Tiefgarage",
    "Großprojekt mit mehreren Abschnitten",
    "Noch offen"
]
